import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { ScholarshipReportFilters } from '../operations/get-scholarship-report-operation';
import { StudentScholarshipsStatus } from '../../../../../api/types/students/student-scholarships/student-scholarship-entity';
import { Calendar } from 'primereact/calendar';

interface ScholarshipSideBarFilterProps {
  onSearch: (filters: ScholarshipReportFilters) => void;
}

const getStatusDisplayText = (status: StudentScholarshipsStatus): string => {
  switch (status) {
    case StudentScholarshipsStatus.PENDING:
      return 'ממתינה';
    case StudentScholarshipsStatus.APPROVED:
      return 'מאושרת';
    case StudentScholarshipsStatus.FAILED:
      return 'נכשלה';
    case StudentScholarshipsStatus.CANCELLED:
      return 'בוטלה';
    default:
      return status;
  }
};

export const ScholarshipSideBarFilter: React.FC<ScholarshipSideBarFilterProps> =
  observer(({ onSearch }) => {
    const [scholarshipSumStart, setScholarshipSumStart] = useState<
      number | null
    >(null);
    const [scholarshipSumEnd, setScholarshipSumEnd] = useState<number | null>(
      null,
    );
    const [selectedStatuses, setSelectedStatuses] = useState<
      StudentScholarshipsStatus[]
    >([]);
    const [createdAtStart, setCreatedAtStart] = useState<Date | null>(null);
    const [createdAtEnd, setCreatedAtEnd] = useState<Date | null>(null);

    const statusOptions = (
      Object.values(StudentScholarshipsStatus) as StudentScholarshipsStatus[]
    ).map((value) => ({
      label: getStatusDisplayText(value),
      value,
    }));

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        marginBottom: '8px',
        color: '#333',
      },
      sectionTitle: {
        fontSize: '1rem',
        fontWeight: 600,
        marginTop: '8px',
        marginBottom: '4px',
        color: '#374151',
      },
      label: {
        fontSize: '0.9rem',
        color: '#6b7280',
        marginBottom: '4px',
      },
      buttonContainer: {
        marginTop: '16px',
        paddingTop: '8px',
        borderTop: '1px solid #e5e7eb',
      },
    };

    const handleSearch = () => {
      const filters: ScholarshipReportFilters = {
        scholarshipSumStart,
        scholarshipSumEnd,
        createdAtStart,
        createdAtEnd,
        statuses: selectedStatuses,
      };
      onSearch(filters);
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>סינונים</div>

        <div>
          <div style={styles.sectionTitle}>טווח סכומים</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div>
              <div style={styles.label}>החל מ</div>
              <InputNumber
                value={scholarshipSumStart}
                onValueChange={(e) => setScholarshipSumStart(e.value)}
                mode="currency"
                currency="ILS"
                locale="he-IL"
                style={{ width: '100%', direction: 'ltr' }}
                inputStyle={{ direction: 'rtl', textAlign: 'right' }}
                min={0}
              />
            </div>
            <div>
              <div style={styles.label}>עד</div>
              <InputNumber
                value={scholarshipSumEnd}
                onValueChange={(e) => setScholarshipSumEnd(e.value)}
                mode="currency"
                currency="ILS"
                locale="he-IL"
                style={{ width: '100%', direction: 'ltr' }}
                inputStyle={{ direction: 'rtl', textAlign: 'right' }}
                min={0}
              />
            </div>
          </div>
        </div>

        <div>
          <div style={styles.sectionTitle}>סטטוס</div>
          <MultiSelect
            value={selectedStatuses}
            options={statusOptions}
            onChange={(e) =>
              setSelectedStatuses(e.value as StudentScholarshipsStatus[])
            }
            optionLabel="label"
            optionValue="value"
            placeholder="בחר סטטוסים"
            display="chip"
            style={{ width: '100%' }}
          />
        </div>

        <div>
          <div style={styles.sectionTitle}>תאריך יצירת מלגה</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div>
              <div style={styles.label}>מתאריך</div>
              <Calendar
                value={createdAtStart}
                onChange={(e) => setCreatedAtStart(e.value as Date)}
                dateFormat="dd/mm/yy"
                showIcon
                iconPos="right"
                style={{ width: '100%', direction: 'ltr' }}
                inputStyle={{ direction: 'rtl', textAlign: 'right' }}
                placeholder="בחר תאריך התחלה"
              />
            </div>
            <div>
              <div style={styles.label}>עד תאריך</div>
              <Calendar
                value={createdAtEnd}
                onChange={(e) => setCreatedAtEnd(e.value as Date)}
                dateFormat="dd/mm/yy"
                showIcon
                iconPos="right"
                style={{ width: '100%', direction: 'ltr' }}
                inputStyle={{ direction: 'rtl', textAlign: 'right' }}
                placeholder="בחר תאריך סיום"
              />
            </div>
          </div>
        </div>

        <div style={styles.buttonContainer}>
          <Button
            label="הצג דו\ח"
            icon="pi pi-search"
            onClick={handleSearch}
            style={{
              width: '100%',
              backgroundColor: '#2196F3',
              border: 'none',
            }}
          />
        </div>
      </div>
    );
  });
